import React from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Stack,
  Paper,
} from "@mui/material";
import { ErrorOutline, Refresh, Home, BugReport } from "@mui/icons-material";
import { flexCenter, flexColumn } from "../../theme/responsive";

/**
 * Error Display Component
 * Reusable component for displaying errors consistently
 */
export const ErrorDisplay = ({
  error,
  title = "Something went wrong",
  message,
  showDetails = false,
  onRetry,
  onHome,
  severity = "error",
  fullHeight = false,
}) => {
  const errorMessage =
    message || error?.message || error?.error || "An unexpected error occurred";

  return (
    <Box
      sx={{
        ...flexCenter,
        ...flexColumn,
        minHeight: fullHeight ? "50vh" : "auto",
        p: { xs: 2, md: 4 },
        width: "100%",
      }}
    >
      <Paper
        elevation={2}
        sx={{
          p: { xs: 3, md: 4 },
          maxWidth: 600,
          width: "100%",
          textAlign: "center",
        }}
      >
        <ErrorOutline
          sx={{
            fontSize: { xs: 48, md: 64 },
            color: severity === "error" ? "error.main" : "warning.main",
            mb: 2,
          }}
        />

        <Typography variant="h5" gutterBottom fontWeight={600}>
          {title}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {errorMessage}
        </Typography>

        {showDetails && error && (
          <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
            <AlertTitle>Error Details</AlertTitle>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {JSON.stringify(error, null, 2)}
            </Typography>
          </Alert>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
        >
          {onRetry && (
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={onRetry}
              size="large"
            >
              Try Again
            </Button>
          )}
          {onHome && (
            <Button
              variant="outlined"
              startIcon={<Home />}
              onClick={onHome}
              size="large"
            >
              Go Home
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

/**
 * Inline Error Alert
 * For displaying errors within forms or sections
 */
export const InlineError = ({ error, onClose }) => {
  if (!error) return null;

  const errorMessage =
    typeof error === "string"
      ? error
      : error?.message || error?.error || "An error occurred";

  return (
    <Alert severity="error" onClose={onClose} sx={{ mb: 2 }}>
      {errorMessage}
    </Alert>
  );
};

/**
 * Error Boundary Component
 * Catches React errors in component tree
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Log to error reporting service if available
    if (window.errorReporter) {
      window.errorReporter.log(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/dashboard";
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === "development";

      return (
        <Box
          sx={{
            minHeight: "100vh",
            ...flexCenter,
            ...flexColumn,
            bgcolor: "background.default",
            p: 2,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 5 },
              maxWidth: 700,
              width: "100%",
              textAlign: "center",
            }}
          >
            <BugReport
              sx={{
                fontSize: { xs: 60, md: 80 },
                color: "error.main",
                mb: 2,
              }}
            />

            <Typography variant="h4" gutterBottom fontWeight={700}>
              Oops! Something went wrong
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              We apologize for the inconvenience. The error has been logged and
              we'll look into it.
            </Typography>

            {isDevelopment && this.state.error && (
              <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
                <AlertTitle>Development Error Details</AlertTitle>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontSize: "0.75rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 200,
                    overflow: "auto",
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo &&
                    "\n\n" + this.state.errorInfo.componentStack}
                </Typography>
              </Alert>
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
            >
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={this.handleReload}
                size="large"
              >
                Reload Page
              </Button>
              <Button
                variant="outlined"
                startIcon={<Home />}
                onClick={this.handleHome}
                size="large"
              >
                Go to Dashboard
              </Button>
            </Stack>

            {isDevelopment && (
              <Button
                variant="text"
                size="small"
                onClick={this.handleReset}
                sx={{ mt: 2 }}
              >
                Reset Error Boundary (Dev Only)
              </Button>
            )}
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorDisplay;
